import React, { useState, useCallback, useEffect } from "react";
import AddInBondHeader from "./addinbondheader";
import AddInBondEntryForm from "./addinbondentryform";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import { useLoader } from "../../context/loadercontext";
import CancelButton from "../common/ui/cancelbutton";
import Button from "../common/ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import WarningModal from "../common/ui/warningmodel";

export default function AddInBond() {
  const { setLoading } = useLoader();
  const navigate = useNavigate();
  const [binumber, setBINumber] = useState([]);
  const [loadingBINumber, setLoadingBINumber] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [records, setRecords] = useState([]); // moved here from child
  const { logout } = useAuth();
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [formData, setFormData] = useState({
    biNumber: "",
    beNumber: "",
    beDate: "",
    inbondDate: "",
    material: null,
    weight: "",
    invoiceValue: "",
    dollar: "",
    price: "",
    duty: "",
    assessment: "",
    totalAmount: "",
  });

  // === Utilities ===
  const updateFormData = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // === Fetch BI Numbers ===
  const fetchBINumber = useCallback(async () => {
    setLoading(true);
    const startTime = Date.now();
    try {
      setLoadingBINumber(true);
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.GET_ALL_BI_NUMBER}`,
        { method: "GET", headers: getHeaders(true) }
      );
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.GET_ALL_BI_NUMBER}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }

      const result = await response.json();
      if (result.status === 200 && Array.isArray(result.data)) {
        setBINumber(
          result.data.map((item) => ({ label: item.binumber, value: item.id }))
        );
      } else {
        setBINumber([]);
      }
    } catch (error) {
      console.error("Error fetching BI number:", error);
      setBINumber([]);
    } finally {
      const elapsed = Date.now() - startTime;
      const remainingDelay = Math.max(0, 1000 - elapsed);
      setTimeout(() => setLoading(false), remainingDelay);
      setLoadingBINumber(false);
    }
  }, [setLoading, logout]);

  // === Fetch Materials ===
  const fetchMaterials = useCallback(async () => {
    setLoading(true);
    const start = Date.now();
    try {
      setLoadingMaterials(true);
      const url = new URL(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.GET_NAME}`);
      url.searchParams.append("mode", "add");

      let res = await fetch(url.toString(), {
        method: "GET",
        headers: getHeaders(true),
      });

      // -------------------------
      // 🔥 Handle 401 token expired
      // -------------------------
      if (res.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          res = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.GET_NAME}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }
      const result = await res.json();
      if (result.status === 200 && Array.isArray(result.data)) {
        setMaterials(
          result.data.map((m) => ({
            label: `${m.material_short_code.toUpperCase()}`,
            value: m.material_id,
            nameOnly: m.material_name
          }))
        );
      } else {
        setMaterials([]);
      }
    } catch (err) {
      console.error("Error fetching materials:", err);
      setMaterials([]);
    } finally {
      const elapsed = Date.now() - start;
      setTimeout(() => setLoading(false), Math.max(0, 1000 - elapsed));
      setLoadingMaterials(false);
    }
  }, [setLoading, logout]);

  useEffect(() => {
    fetchBINumber();
    fetchMaterials();
  }, [fetchBINumber, fetchMaterials]);

  const canSubmit =
    formData.biNumber &&
    formData.beNumber &&
    formData.beDate &&
    formData.inbondDate;

  const handleSubmit = async () => {
    if (records.length === 0) {
      setWarningMessage("Please add at least one material entry!");
      setWarningVisible(true);
      return;
    }

    const payload = {
      bi_number: `BI ${formData.biNumber}`,
      be_number: formData.beNumber,
      be_date: formData.beDate,
      inbond_date: formData.inbondDate,
      total_duty_inbond_amount_inr: records.reduce((acc, r) => acc + Number(r.duty || 0), 0),
      total_weight: records.reduce((acc, r) => acc + Number(r.weight || 0), 0),
      total_assessment_amount_inr: records.reduce((acc, r) => acc + Number(r.assessment || 0), 0),
      total_material_amount_usd: records.reduce((acc, r) => acc + Number(r.totalAmount || 0), 0),
      inbondchild: records.map((r) => ({
        material_master_id: r.material,
        duty_inbond_amount_inr: Number(r.duty || 0),
        weight: Number(r.weight || 0),
        invoice_amount_usd: Number(r.invoiceValue || 0),
        assessment_amount_inr: Number(r.assessment || 0),
        dollar_inr: Number(r.dollar || 0),
        price: Number(r.price || 0),
        material_amount_usd: Number(r.totalAmount || 0),
      })),
    };

    try {
      setLoading(true);

      // Helper to make the API call
      const callApi = async () =>
        await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.CREATE}`, {
          method: "POST",
          headers: { ...getHeaders(true), "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

      // Initial API call
      let response = await callApi();

      // -------------------------
      // 🔥 Handle 401 token expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry the same API call
          response = await callApi();
        } else {
          console.error("Refresh token failed. Logging out...");
          logout();
          return;
        }
      }

      const result = await response.json();

      if (result.status === 201 || result.status === 200) {
        setRecords([]); // clear all entries
        navigate("/in-bond", {
          state: {
            showToast: true,
            message: result.message || "In-bond created successfully!",
          },
        });
      } else {
        setWarningMessage(result.message || "Failed to create in-bond");
        setWarningVisible(true);
      }
    } catch (err) {
      console.error("Error submitting inbond:", err);
      setWarningMessage("Something went wrong.");
      setWarningVisible(true);
    } finally {
      setLoading(false);
    }
  };



  return (
    <>
      <div className="w-full px-2 md:px-4">
        <AddInBondHeader />
        <AddInBondEntryForm
          formData={formData}
          updateFormData={updateFormData}
          biNumberList={binumber}
          loadingBINumber={loadingBINumber}
          materialList={materials}
          loadingMaterials={loadingMaterials}
          records={records}       // pass records to child
          setRecords={setRecords} // pass setRecords to child
        />
        <div className="flex justify-end gap-4 mt-4">
          <CancelButton onClick={() => navigate("/in-bond")}>Cancel</CancelButton>
          <Button
            bgColor="bg-colorButton"
            textColor="text-white"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
      </div>
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}

    </>
  );
}
